<template>
  <!--
    ChatGPTEditor wraps its fields in <form @input="updateAction">, a DELEGATED handler that writes
    action[event.target.name]. These checkboxes have no name, so without .stop every toggle would
    write a junk action[''] key — which then never matches the saved answer and leaves the editor
    permanently reporting unsaved changes. The tool selection is committed by toggle() alone.
  -->
  <div class="col-span-2" @input.stop @change.stop>
    <base-label id="chatGptTools"
                label="Tools"
                tooltip="API calls the AI may decide to make while writing this answer. It only ever sees the tools selected here."
    />

    <div v-if="!tools.length" class="h-10 mt-1 flex items-center">
      <span>None exist.</span>
      <router-link :to="{ name: ROUTE_NAMES.tools }" class="ml-1 underline hover:text-black">
        Create?
      </router-link>
    </div>

    <div v-else class="mt-1 rounded border border-solid border-gray divide-x-0 divide-y divide-solid divide-soft">
      <label v-for="tool in tools"
             :key="tool.id"
             :for="`chatGptTool-${tool.id}`"
             class="flex items-start gap-2 p-3"
             :class="isDisabled ? 'cursor-not-allowed' : 'cursor-pointer hover:bg-soft'"
      >
        <checkbox :id="`chatGptTool-${tool.id}`"
                  :is-checked="isSelected(tool.id)"
                  :is-disabled="isDisabled"
                  class="mt-0.5 flex-shrink-0"
                  @update:isChecked="toggle(tool.id, $event)"
        />
        <span class="flex flex-col min-w-0">
          <span class="font-mono text-sm text-gray-dark">{{ tool.name }}</span>
          <span class="text-sm text-gray truncate">{{ tool.description }}</span>
        </span>
      </label>
    </div>

    <span v-if="tools.length" class="block mt-1 text-xs text-gray">
      Every selected tool is described to the AI on each message of this answer, so select only what
      this answer needs.
    </span>
  </div>
</template>

<script>
import Checkbox from '@/components/preview/Checkbox'
import conversationModule from '@/store/modules/conversation.module'
import toolsModule from '@/store/modules/tools.module'
import { ROUTE_NAMES } from '@/router'

export default {
  name: 'ChatGPTEditorTools',

  components: { Checkbox },

  props: {
    action: {
      type: Object,
      required: true
    },
    actionId: {
      type: String,
      required: true
    },
    isDisabled: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return { ROUTE_NAMES }
  },

  computed: {
    tools: toolsModule.tools,

    // Tools are stored on the action as ids; the assembler swaps them for full definitions at deploy.
    // An action authored before tools existed has no field at all, hence the fallback.
    selectedIds() {
      return this.action.tools || []
    }
  },

  methods: {
    isSelected(toolId) {
      return this.selectedIds.includes(toolId)
    },

    toggle(toolId, isChecked) {
      if (this.isDisabled) return

      // Selection ORDER is preserved — it is the order the model sees the tools in — so a re-check
      // appends rather than restoring the original position.
      const tools = isChecked ?
        [...this.selectedIds, toolId] :
        this.selectedIds.filter(id => id !== toolId)

      conversationModule.updateChatGPTAction(this.actionId, { ...this.action, tools })
    }
  },

  mounted() {
    // The list is org-wide and small; fetching per editor keeps it fresh after a tool is added in
    // another tab, and the store already holds the result for the rest of the session.
    if (!this.tools.length) toolsModule.fetchTools()
  }
}
</script>
